/**
 * Helpers for carrying a "return URL" through the whole auth funnel:
 * login popup → register → verify email → login → back to where the user was.
 *
 * Only same-origin relative paths are ever accepted, to avoid open redirects.
 */

const PENDING_CALLBACK_KEY = 'pendingAuthCallbackUrl';
const LEGACY_SESSION_KEY = 'redirectAfterLogin';
/** A pending callback survives the email round-trip for up to 7 days. */
const PENDING_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Returns a safe relative path ("/foo?bar=1#x") or null.
 * Rejects protocol-relative ("//evil.com"), backslash tricks, absolute URLs to
 * other origins, and auth pages (which would cause redirect loops).
 */
export function sanitizeCallbackUrl(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;
  let value = raw.trim();
  if (!value) return null;

  // Accept absolute URLs only if they point at our own origin, then strip the origin.
  if (/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(value)) {
    if (typeof window === 'undefined') return null;
    try {
      const url = new URL(value);
      if (url.origin !== window.location.origin) return null;
      value = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }

  if (!value.startsWith('/')) return null;
  if (value.startsWith('//') || value.startsWith('/\\')) return null;
  for (let i = 0; i < value.length; i++) {
    if (value.charCodeAt(i) < 0x20) return null; // control characters
  }
  if (value === '/auth' || value.startsWith('/auth/')) return null;
  return value;
}

/** Reads and validates `?callbackUrl=` from the current location (client only). */
export function getCallbackUrlFromLocation(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return sanitizeCallbackUrl(params.get('callbackUrl'));
  } catch {
    return null;
  }
}

/** Remember a callback across the register → verify-email round trip. */
export function storePendingCallbackUrl(url: string | null | undefined): void {
  const safe = sanitizeCallbackUrl(url);
  if (!safe || typeof window === 'undefined') return;
  try {
    localStorage.setItem(PENDING_CALLBACK_KEY, JSON.stringify({ url: safe, at: Date.now() }));
  } catch {
    // storage unavailable — ignore
  }
}

export function getPendingCallbackUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PENDING_CALLBACK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { url?: string; at?: number };
    if (!parsed?.url || !parsed.at || Date.now() - parsed.at > PENDING_TTL_MS) {
      localStorage.removeItem(PENDING_CALLBACK_KEY);
      return null;
    }
    return sanitizeCallbackUrl(parsed.url);
  } catch {
    return null;
  }
}

export function clearPendingCallbackUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(PENDING_CALLBACK_KEY);
  } catch {
    // ignore
  }
}

/**
 * Where to send the user after a successful login, in priority order:
 * explicit `?callbackUrl=`, legacy sessionStorage `redirectAfterLogin`,
 * pending callback stored at registration, then "/".
 * Does not consume anything — call `clearAuthRedirectState()` once you navigate.
 */
export function resolvePostLoginRedirect(): string {
  const fromQuery = getCallbackUrlFromLocation();
  if (fromQuery) return fromQuery;
  if (typeof window !== 'undefined') {
    try {
      const legacy = sanitizeCallbackUrl(sessionStorage.getItem(LEGACY_SESSION_KEY));
      if (legacy) return legacy;
    } catch {
      // ignore
    }
  }
  return getPendingCallbackUrl() || '/';
}

export function clearAuthRedirectState(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // ignore
  }
  clearPendingCallbackUrl();
}

/** Build an auth page href that carries the callback, e.g. authHref('/auth/login', '/create/x'). */
export function authHref(path: string, callbackUrl: string | null | undefined): string {
  const safe = sanitizeCallbackUrl(callbackUrl);
  return safe ? `${path}?callbackUrl=${encodeURIComponent(safe)}` : path;
}
